import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { CustomSelect } from '@/components/CustomSelect';
import { Slider } from '@/components/ui/slider';
import { Search, ExternalLink, Plus, Bookmark, X, ChevronDown, ChevronUp, Zap, Building2, MapPin, Tag, Star } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';
function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }



export default function MarketDeals() {
  const navigate = useNavigate();
  const { assets } = usePortfolio();
  const [listings, setListings] = useState([]);
  const [lastScraped, setLastScraped] = useState(null);
  const [loadingListings, setLoadingListings] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [search, setSearch] = useState('');
  const [cityF, setCityF] = useState('all');
  const [segF, setSegF] = useState('all');
  const [minMatch, setMinMatch] = useState(0);
  const [rentedF, setRentedF] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [sims, setSims] = useState({});
  const getSim = (id) => sims[id] || { priceDelta: 0, rentDelta: 0, ltv: 65, rate: 3.5 };

  useEffect(() => {
    api.get('/market/listings').then(({ data }) => {
      setListings(data.listings || []);
      setLastScraped(data.last_scraped);
    }).catch(console.error).finally(() => setLoadingListings(false));
  }, []);

  const triggerScrape = async () => {
    setScraping(true);
    try {
      await api.post('/market/scrape');
      const { data } = await api.get('/market/listings');
      setListings(data.listings || []);
      setLastScraped(data.last_scraped);
    } catch (e) { console.error(e); }
    finally { setScraping(false); }
  };

  // Portfolio context for matching
  const pfCtx = useMemo(() => {
    if (!assets.length) return { totalValue: 0, totalRent: 0, segments: {}, cities: {}, avgYield: 0, resPct: 0, ltv: 60 };
    const tv = assets.reduce((s, a) => s + (a.metrics?.market_value || 0), 0);
    const tr = assets.reduce((s, a) => s + (a.annual_rent || 0), 0);
    const segs = {}; assets.forEach(a => { const t = a.property_type || 'residential'; segs[t] = (segs[t] || 0) + 1; });
    const cities = {}; assets.forEach(a => { cities[a.city || '?'] = (cities[a.city || '?'] || 0) + 1; });
    const resPct = (segs.residential || 0) / assets.length * 100;
    const td = assets.reduce((s, a) => s + (a.metrics?.loan_amount || 0), 0);
    return { totalValue: tv, totalRent: tr, segments: segs, cities, avgYield: tv > 0 ? tr / tv * 100 : 0, resPct, ltv: tv > 0 ? td / tv * 100 : 60, assetCount: assets.length };
  }, [assets]);

  // Compute match scores from DB listings
  const enriched = useMemo(() => {
    return listings.map(l => {
      const id = l.listing_id || l.id || Math.random().toString();
      const sim = getSim(id);
      const simPrice = l.price * (1 + sim.priceDelta / 100);
      const simRent = (l.rent || 0) * (1 + sim.rentDelta / 100);
      const la = simPrice * sim.ltv / 100;
      const noi = simRent * 0.88; // 12% opex estimate
      const ads = mp(la, sim.rate / 100, 30) * 12;
      const ncf = noi - ads;
      const gy = simPrice > 0 ? simRent / simPrice * 100 : 0;
      const ny = simPrice > 0 ? noi / simPrice * 100 : 0;
      const dscr = ads > 0 ? noi / ads : 0;
      const dy = la > 0 ? noi / la * 100 : 0;
      const ei = simPrice - la;
      const cfs = [-ei, ...Array(4).fill(ncf), ncf + simPrice * 1.08 - la];
      let irr = 0.1; for (let i = 0; i < 60; i++) { const npv = cfs.reduce((s, cf, j) => s + cf / Math.pow(1 + irr, j), 0); const dnpv = cfs.reduce((s, cf, j) => s + -j * cf / Math.pow(1 + irr, j + 1), 0); if (Math.abs(dnpv) < 1e-10) break; irr -= npv / dnpv; } irr = (isFinite(irr) ? irr : 0) * 100;

      // Strategy fit (30%)
      const isNewCity = !pfCtx.cities[l.city];
      const diversifies = l.segment !== 'residential' && pfCtx.resPct > 55;
      const stratFit = Math.min(30, (isNewCity ? 8 : 3) + (diversifies ? 10 : 3) + (l.price < pfCtx.totalValue * 0.2 ? 6 : 2) + 3);

      // Return fit (30%)
      const retFit = Math.min(30, (ny >= 5 ? 12 : ny >= 4 ? 8 : 3) + (irr >= 12 ? 10 : irr >= 9 ? 7 : 2) + (ny - 2.48 > 1.5 ? 8 : ny - 2.48 > 1 ? 5 : 2));

      // Risk fit (25%)
      const riskFit = Math.min(25, (l.rented ? 10 : 2) + (l.walt > 3 ? 8 : l.walt > 1 ? 5 : 1) + (l.label <= 'B' ? 5 : 2) + 2);

      // Finance fit (15%)
      const finFit = Math.min(15, (dscr >= 1.25 ? 8 : dscr >= 1.1 ? 5 : 1) + (dy >= 7 ? 5 : dy >= 5 ? 3 : 1) + 2);

      const matchPct = stratFit + retFit + riskFit + finFit;
      const confidence = l.rent > 0 && l.rented ? 'High' : l.rent > 0 ? 'Medium' : 'Low';

      // Target prices
      const targetIrrPrice = simRent > 0 ? simRent * 0.88 / 0.055 : 0; // ~5.5% cap
      const walkAway = l.price * 1.02;

      return { ...l, id, simPrice, simRent, la, noi, ncf, gy, ny, dscr, dy, irr, ei,
        matchPct, stratFit, retFit, riskFit, finFit, confidence,
        targetIrrPrice, walkAway };
    }).sort((a, b) => b.matchPct - a.matchPct);
  }, [pfCtx, sims]);

  // Filter
  const filtered = useMemo(() => {
    let list = enriched;
    if (search) { const s = search.toLowerCase(); list = list.filter(l => l.title.toLowerCase().includes(s) || l.city.toLowerCase().includes(s)); }
    if (cityF !== 'all') list = list.filter(l => l.city === cityF);
    if (segF !== 'all') list = list.filter(l => l.segment === segF);
    if (rentedF !== 'all') list = list.filter(l => rentedF === 'rented' ? l.rented : !l.rented);
    if (minMatch > 0) list = list.filter(l => l.matchPct >= minMatch);
    return list;
  }, [enriched, search, cityF, segF, rentedF, minMatch]);

  const cities = [...new Set(listings.map(l => l.city))].sort();
  const segments = [...new Set(listings.map(l => l.segment))].sort();
  const highMatch = enriched.filter(l => l.matchPct >= 70).length;
  const avgYield = enriched.length > 0 ? enriched.reduce((s, l) => s + l.ny, 0) / enriched.length : 0;
  const newLast7 = listings.filter(l => { const d = new Date(l.date); return (Date.now() - d.getTime()) < 7*24*60*60*1000; }).length;

  return (
    <div className="space-y-7 animate-fade-in" data-testid="market-deals-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Market Deals — NL Commercial</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Acquisition Radar</h1>
          {lastScraped && <p className="text-[10px] text-[#D9D3C8] font-sans mt-0.5">Last updated: {new Date(lastScraped).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} — Auto-refresh: monthly</p>}
        </div>
        <button onClick={triggerScrape} disabled={scraping}
          className="flex items-center gap-1.5 px-4 py-2 border border-[#E2DDD3] text-[#5A5A5A] rounded-lg text-[11px] font-sans font-medium hover:border-[#1F3A34]/30 hover:text-[#1F3A34] transition-colors disabled:opacity-50">
          {scraping ? 'Refreshing...' : 'Refresh Listings'}
        </button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { l: 'New (7d)', v: newLast7.toString() },
          { l: 'Match >70%', v: highMatch.toString(), c: '#2E6F57' },
          { l: 'Avg Net Yield', v: `${avgYield.toFixed(1)}%` },
          { l: 'Avg Ticket', v: fmt(enriched.reduce((s, l) => s + l.price, 0) / enriched.length) },
          { l: 'Top City', v: 'Amsterdam' },
          { l: 'Top Segment', v: 'Office' },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <p className="text-[9px] text-[#8C877F] font-sans font-medium">{k.l}</p>
            <p className="text-[18px] font-sans font-semibold leading-none mt-1" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-[260px]">
          <Search className="w-4 h-4 text-[#8C877F] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..."
            className="w-full pl-9 pr-3 py-2 bg-[#F4F1EB] border-none rounded-lg text-[13px] font-sans placeholder:text-[#D9D3C8] focus:outline-none focus:ring-1 focus:ring-[#1F3A34]/20" />
        </div>
        <CustomSelect value={cityF} onChange={setCityF} options={[{ value: 'all', label: 'All Cities' }, ...cities.map(c => ({ value: c, label: c }))]} testId="city-f" />
        <CustomSelect value={segF} onChange={setSegF} options={[{ value: 'all', label: 'All Segments' }, ...segments.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))]} testId="seg-f" />
        <CustomSelect value={rentedF} onChange={setRentedF} options={[{ value: 'all', label: 'All Status' }, { value: 'rented', label: 'Rented' }, { value: 'empty', label: 'Vacant' }]} />
        <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E2DDD3] rounded-lg">
          <span className="text-[11px] text-[#8C877F] font-sans">Min Match:</span>
          <input type="number" value={minMatch} onChange={e => setMinMatch(parseInt(e.target.value) || 0)} className="w-10 text-[12px] font-sans font-semibold text-[#1F3A34] bg-transparent border-none focus:outline-none text-center" />
          <span className="text-[11px] text-[#8C877F]">%</span>
        </div>
      </div>

      {/* Listings */}
      <div className="space-y-3">
        {filtered.map(l => {
          const isExp = expanded === l.id;
          const sim = getSim(l.id);
          const matchColor = l.matchPct >= 70 ? '#2E6F57' : l.matchPct >= 50 ? '#A67C52' : '#8A3C3C';
          const matchBg = l.matchPct >= 70 ? '#E6EFEA' : l.matchPct >= 50 ? '#FDF8F0' : '#FDF5F5';
          return (
            <div key={l.id} className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <div className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-[#F4F1EB]/30 transition-colors" onClick={() => setExpanded(isExp ? null : l.id)}>
                {/* Match badge */}
                <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: matchBg }}>
                  <span className="text-[16px] font-sans font-bold" style={{ color: matchColor }}>{l.matchPct}</span>
                  <span className="text-[7px] font-sans font-medium" style={{ color: matchColor }}>MATCH</span>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[15px] text-[#1C1C1C] font-sans font-semibold truncate">{l.title}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F4F1EB] text-[#5A5A5A] font-sans font-medium">{l.segment}</span>
                    {!l.rented && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FDF8F0] text-[#A67C52] font-medium">Vacant</span>}
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F4F1EB] text-[#8C877F] font-medium">{l.label}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[#8C877F] font-sans">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.city}</span>
                    <span>{l.sqm} m²</span>
                    <span>{l.source}</span>
                    <span>{l.date}</span>
                    <span className={`font-medium ${l.confidence === 'High' ? 'text-[#2E6F57]' : l.confidence === 'Medium' ? 'text-[#A67C52]' : 'text-[#8C877F]'}`}>{l.confidence} conf.</span>
                  </div>
                </div>
                {/* Key metrics */}
                <div className="flex items-center gap-5 text-right">
                  {[
                    { l: 'Price', v: fmt(l.price) },
                    { l: 'Net Yield', v: `${l.ny.toFixed(1)}%`, c: sColor(l.ny, 5, 4) },
                    { l: 'IRR', v: `${l.irr.toFixed(1)}%`, c: sColor(l.irr, 10, 8) },
                    { l: 'DSCR', v: l.dscr.toFixed(2), c: sColor(l.dscr, 1.25, 1.1) },
                  ].map(k => (
                    <div key={k.l} className="hidden md:block">
                      <p className="text-[8px] text-[#8C877F]">{k.l}</p>
                      <p className="text-[14px] font-sans font-semibold" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p>
                    </div>
                  ))}
                  {isExp ? <ChevronUp className="w-4 h-4 text-[#8C877F]" /> : <ChevronDown className="w-4 h-4 text-[#8C877F]" />}
                </div>
              </div>

              {/* Expanded Detail */}
              {isExp && (
                <div className="px-5 pb-5 border-t border-[#E2DDD3] pt-5 space-y-5 animate-fade-in">
                  {/* Match Breakdown */}
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { l: 'Strategy Fit', v: l.stratFit, max: 30 },
                      { l: 'Return Fit', v: l.retFit, max: 30 },
                      { l: 'Risk Fit', v: l.riskFit, max: 25 },
                      { l: 'Finance Fit', v: l.finFit, max: 15 },
                    ].map(s => (
                      <div key={s.l} className="text-center">
                        <div className="w-full h-2 bg-[#F4F1EB] rounded-full mb-1"><div className="h-full rounded-full" style={{ width: `${s.v/s.max*100}%`, backgroundColor: s.v/s.max >= 0.6 ? '#2E6F57' : s.v/s.max >= 0.4 ? '#A67C52' : '#8C877F' }} /></div>
                        <p className="text-[10px] text-[#8C877F] font-sans">{s.l} <strong>{s.v}/{s.max}</strong></p>
                      </div>
                    ))}
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                    {[
                      { l: 'Gross Yield', v: `${l.gy.toFixed(1)}%` },
                      { l: 'Net Yield', v: `${l.ny.toFixed(1)}%`, c: sColor(l.ny, 5, 4) },
                      { l: 'IRR', v: `${l.irr.toFixed(1)}%`, c: sColor(l.irr, 10, 8) },
                      { l: 'DSCR', v: l.dscr.toFixed(2), c: sColor(l.dscr, 1.25, 1.1) },
                      { l: 'LTV', v: `${sim.ltv}%` },
                      { l: 'Debt Yield', v: `${l.dy.toFixed(1)}%` },
                      { l: 'Net CF/yr', v: fmt(l.ncf), c: l.ncf >= 0 ? '#2E6F57' : '#8A3C3C' },
                      { l: 'Equity Req', v: fmt(l.ei) },
                    ].map(k => (
                      <div key={k.l}><p className="text-[9px] text-[#8C877F]">{k.l}</p><p className="text-[14px] font-sans font-semibold" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p></div>
                    ))}
                  </div>

                  {/* Sliders */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { l: 'Price', v: sim.priceDelta, set: v => setSims(p => ({ ...p, [l.id]: { ...getSim(l.id), priceDelta: v } })), min: -20, max: 10, suffix: '%' },
                      { l: 'Rent', v: sim.rentDelta, set: v => setSims(p => ({ ...p, [l.id]: { ...getSim(l.id), rentDelta: v } })), min: -10, max: 25, suffix: '%' },
                      { l: 'LTV', v: sim.ltv, set: v => setSims(p => ({ ...p, [l.id]: { ...getSim(l.id), ltv: v } })), min: 50, max: 75, suffix: '%' },
                      { l: 'Rate', v: sim.rate, set: v => setSims(p => ({ ...p, [l.id]: { ...getSim(l.id), rate: v } })), min: 2, max: 5.5, step: 0.1, suffix: '%' },
                    ].map(s => (
                      <div key={s.l} className="bg-[#F4F1EB]/50 rounded-lg p-3 space-y-1.5">
                        <div className="flex justify-between"><span className="text-[11px] text-[#5A5A5A] font-sans">{s.l}</span><span className="text-[12px] font-sans font-semibold text-[#1F3A34]">{s.v > 0 && s.l === 'Price' ? '+' : ''}{s.v}{s.suffix}</span></div>
                        <Slider value={[s.v]} onValueChange={v => s.set(v[0])} min={s.min} max={s.max} step={s.step || 1} className="[&_[role=slider]]:bg-[#1F3A34] [&_[role=slider]]:border-0 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3" />
                      </div>
                    ))}
                  </div>

                  {/* Target Prices */}
                  <div className="flex items-center gap-6 py-2 border-t border-[#E2DDD3]">
                    <div><p className="text-[9px] text-[#8C877F]">Target IRR Price</p><p className="text-[14px] font-sans font-semibold text-[#2E6F57]">{fmt(l.targetIrrPrice)}</p></div>
                    <div><p className="text-[9px] text-[#8C877F]">Walk-Away</p><p className="text-[14px] font-sans font-semibold text-[#8A3C3C]">{fmt(l.walkAway)}</p></div>
                    <div><p className="text-[9px] text-[#8C877F]">€/m²</p><p className="text-[14px] font-sans font-semibold">{fmt(Math.round(l.price / l.sqm))}</p></div>
                    <div><p className="text-[9px] text-[#8C877F]">Huur €/m²/yr</p><p className="text-[14px] font-sans font-semibold">{fmt(Math.round(l.rent / l.sqm))}</p></div>
                  </div>

                  {/* AI Deal Intelligence */}
                  <div className="bg-[#E6EFEA]/50 rounded-lg p-4 space-y-1.5">
                    <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#1F3A34]" /><p className="text-[11px] text-[#1F3A34] font-sans font-semibold">Deal Intelligence</p></div>
                    <p className="text-[12px] text-[#1C1C1C] font-sans pl-5">
                      {l.ny >= 5 ? `Net yield ${l.ny.toFixed(1)}% exceeds portfolio average — strong return fit.` : `Net yield ${l.ny.toFixed(1)}% is ${l.ny < 4 ? 'below' : 'near'} portfolio benchmark.`}
                      {' '}{l.segment !== 'residential' && pfCtx.resPct > 55 ? 'Improves sector diversification.' : ''}
                      {' '}{!pfCtx.cities[l.city] ? `Adds ${l.city} — new geographic exposure.` : ''}
                      {' '}{l.dscr < 1.2 ? 'DSCR below covenant — negotiate price or increase equity.' : ''}
                      {l.matchPct < 70 && ` To reach 70% match: ${l.ny < 4.5 ? 'negotiate rent +10%' : 'reduce price by 8%'}.`}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <button onClick={() => { import('sonner').then(({ toast }) => toast.success(`${l.title} added to pipeline`)); }} className="flex items-center gap-1.5 px-4 py-2 bg-[#1F3A34] text-white rounded-lg text-[12px] font-sans font-medium hover:bg-[#285047]"><Plus className="w-3 h-3" /> Add to Pipeline</button>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 border border-[#E2DDD3] text-[#5A5A5A] rounded-lg text-[12px] font-sans font-medium hover:border-[#1F3A34]/30"><ExternalLink className="w-3 h-3" /> View on {l.source}</a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-center py-8 text-[#8C877F] text-[13px]">No deals match your filters.</p>}
      </div>

      {/* Sources */}
      <div className="text-[9px] text-[#D9D3C8] font-sans">
        <p>Sources: Funda in Business, Bedrijfspand.com, RealNext, Beleggingspanden.nl — Scraped monthly, stored in database. {listings.length} active listings.</p>
      </div>
    </div>
  );
}
